import React from 'react'
import { ViewportPortal, useStore } from '@xyflow/react'
import type { Guide } from './utils/canvasAlignment'
import { DEFAULT_CANVAS_NOTE_WIDTH } from './utils/canvasNoteStyle'
import './HelperLines.scss'

/** Strichstärke in Bildschirmpixeln; gegen den Zoom gerechnet. */
const LINE_THICKNESS = 2

/**
 * Wie weit die Linie über die beteiligten Notizen hinausragt — eine halbe
 * Notizbreite an jedem Ende.
 *
 * In CANVAS-Pixeln, anders als die Strichstärke: Der Überstand soll im
 * Verhältnis zu den Notizen stehen, also mitzoomen. Auf demselben Stück
 * verläuft die Linie ins Nichts (siehe `--guide-fade` in der SCSS), sie endet
 * also nicht mit einer harten Kante mitten auf der Fläche.
 */
const OVERHANG = DEFAULT_CANVAS_NOTE_WIDTH / 2

export interface HelperLinesProps {
  /** Senkrechte Linien (x-Ausrichtung); leer, wenn keine greift. */
  vertical: Guide[]
  /** Waagerechte Linien (y-Ausrichtung). */
  horizontal: Guide[]
}

/**
 * Die Hilfslinien beim Verschieben einer Notiz (siehe CONTEXT.md). Sie
 * zeigen, woran gerade ausgerichtet wird, und verschwinden mit dem Loslassen.
 *
 * Gerendert in React Flows `ViewportPortal` — demselben Container, der auch
 * die Notizen trägt. Damit gelten Canvas-Koordinaten und die Linien werden
 * mitverschoben und -gezoomt, ohne dass die Transformation nachgebaut werden
 * müsste.
 *
 * @example
 * <HelperLines vertical={guides.vertical} horizontal={guides.horizontal} />
 */
export const HelperLines: React.FC<HelperLinesProps> = ({ vertical, horizontal }) => {
  // Die Strichstärke wird gegen den Zoom gerechnet, damit die Linie immer
  // gleich stark erscheint — sonst wäre sie herausgezoomt unsichtbar und
  // herangezoomt ein Balken.
  const zoom = useStore(state => state.transform[2])

  if (vertical.length === 0 && horizontal.length === 0) return null

  const thickness = LINE_THICKNESS / zoom

  return (
    <ViewportPortal>
      {vertical.map(line => (
        <div
          key={`v${line.position}`}
          className="canvas-helper-line canvas-helper-line--vertical"
          style={{
            left: line.position,
            top: line.start - OVERHANG,
            height: line.end - line.start + OVERHANG * 2,
            width: thickness,
            // Auf der Kante zentrieren, nicht daneben setzen.
            marginLeft: -thickness / 2,
            '--guide-fade': `${OVERHANG}px`,
          } as React.CSSProperties}
        />
      ))}
      {horizontal.map(line => (
        <div
          key={`h${line.position}`}
          className="canvas-helper-line canvas-helper-line--horizontal"
          style={{
            top: line.position,
            left: line.start - OVERHANG,
            width: line.end - line.start + OVERHANG * 2,
            height: thickness,
            marginTop: -thickness / 2,
            '--guide-fade': `${OVERHANG}px`,
          } as React.CSSProperties}
        />
      ))}
    </ViewportPortal>
  )
}
